import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import authService from '../services/authService';

const AcceptInvitePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No invite token provided');
        setLoading(false);
        return;
      }

      try {
        const result = await authService.validateInvite(token);
        setEmail(result.email);
        setTokenValid(true);
      } catch (err: any) {
        setError(err.message || 'Invalid or expired invite link');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!token) {
      setError('No invite token');
      return;
    }

    try {
      setSubmitting(true);
      await authService.acceptInvite(token, password, confirmPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to set password');
    } finally {
      setSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  if (loading) {
    return (
      <div className="login-page">
        <div className="login-container">
          <motion.div
            className="login-card"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div className="button-spinner" style={{ margin: '0 auto 1rem', width: '32px', height: '32px' }}></div>
              <p>Validating your invite...</p>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="login-page">
        <div className="login-container">
          <motion.div
            className="login-card"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <CheckCircle size={64} style={{ color: '#10b981', marginBottom: '1rem' }} />
              <h2 style={{ marginBottom: '0.5rem' }}>Account Setup Complete</h2>
              <p style={{ color: '#94a3b8', marginBottom: '1rem' }}>
                Your password has been set successfully. Redirecting to login...
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <motion.div
          className="login-card"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <div className="login-header">
            <div className="login-logo">
              <div className="logo-icon">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="32" height="32" rx="8" fill="#2563eb"/>
                  <path d="M16 8V24M8 16H24" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="16" cy="16" r="2" fill="white"/>
                </svg>
              </div>
              <h1>CLIA Laboratory Analytics</h1>
            </div>
            <p className="login-subtitle">
              Set your password to complete account setup
            </p>
          </div>

          {error && (
            <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {!tokenValid ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <AlertCircle size={48} style={{ color: '#ef4444', marginBottom: '1rem' }} />
              <h3>Invalid Invite</h3>
              <p style={{ color: '#94a3b8' }}>This invite link is invalid or has expired.</p>
              <button
                onClick={() => navigate('/login')}
                style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1.5rem',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                Go to Login
              </button>
            </div>
          ) : (
            <form className="login-form" onSubmit={handleSubmit}>
              <div style={{
                padding: '1rem',
                background: 'rgba(59, 130, 246, 0.1)',
                borderRadius: '8px',
                marginBottom: '1rem',
                fontSize: '0.9rem',
                color: '#94a3b8'
              }}>
                Setting password for: <strong style={{ color: '#e2e8f0' }}>{email}</strong>
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="input-container">
                  <Lock className="input-icon" size={20} />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    minLength={8}
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={submitting}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className="input-container">
                  <Lock className="input-icon" size={20} />
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                    minLength={8}
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={submitting}
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <motion.button
                type="submit"
                className="login-button"
                disabled={submitting || !password || !confirmPassword || password.length < 8}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {submitting ? (
                  <>
                    <div className="button-spinner"></div>
                    Setting up account...
                  </>
                ) : (
                  'Set Password & Continue'
                )}
              </motion.button>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
